/**
 * 
 * Renders a modal workflow for replacing a selected material with a suggested alternative.
 * It shows a searchable table of suggested materials, lets the user choose one,
 * and then confirms the replacement in a secondary confirmation modal.
 *
 * @prop {boolean} show            - Controls visibility of the replace modal.
 * @prop {() => void} onClose      - Callback to close the replace modal.
 * @prop {(item: object) => void} onSelect - Callback invoked with the selected replacement.
 * @prop {object} material         - The original material to be replaced ({ name: { value }, partNo }).
 * @prop {Array<object>} suggestedMaterials - Array of suggestion objects to display in the table.
 *
 * @state {boolean} confirmVisible         - Whether the confirmation modal is visible.
 * @state {object|null} selectedReplacement - The material selected for replacement.
 *
 * @helper openConfirmModal(item)   - Opens the confirmation dialog for a given suggestion.
 * @helper confirmReplacement()     - Invokes onSelect and closes the confirm modal.
 * @helper cancelConfirmation()     - Cancels the confirmation dialog without action.
 */
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { infoSnackBar } from 'src/actions/common.action';
import { Modal, Button } from 'react-bootstrap';
import CustomTable from 'src/components/table/table';
import { toUSD } from 'src/util/currency-formatter';

export default function ReplaceMaterialModal({
  show,
  onClose,
  onSelect,
  material,
  suggestedMaterials
}) {
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [selectedReplacement, setSelectedReplacement] = useState(null);
  const dispatch = useDispatch();

  const openConfirmModal = (item) => {
    setSelectedReplacement(item);
    setConfirmVisible(true);
  };

  const confirmReplacement = () => {
    if (selectedReplacement) {
      onSelect(selectedReplacement);
      setConfirmVisible(false);
      dispatch(infoSnackBar('Replacement added. Please click Save button to apply changes.'));
    }
  };

  const cancelConfirmation = () => {
    setSelectedReplacement(null);
    setConfirmVisible(false);
  };

  // Flatten out the fields you want to be searchable
  const tableData = suggestedMaterials.map((mat, i) => ({
    ...mat,
    serial: i + 1,
    item: mat.item || mat.item_description || '',
    catalog: mat.category?.name || '',
    partNo: mat.partNo || mat.part_no || '',
    manufacturer: mat.manufacturer || ''
  }));

  const columns = [
    { title: 'S No', field: 'serial' },
    { title: 'Item', field: 'item' },
    { title: 'Catalog', field: 'catalog' },
    { title: 'Category', field: 'manufacturer' },
    { title: 'Part No.', field: 'partNo' },
    {
      title: 'Cost',
      render: (rowData) => toUSD(rowData.cost)
    },
    {
      title: 'Actions',
      render: (item) => (
        <Button
          variant="primary" className='replace-duplicate-btn btn-sm' data-cy="replace-material"
          onClick={() => openConfirmModal(item)}
        >
          REPLACE
        </Button>
      )
    }
  ];

  return (
    <>
      <Modal
        show={show}
        onHide={onClose}
        size="xl"
        backdrop="static"
        keyboard={false}
        centered
        scrollable
        className="replace-modal"
      >
        <Modal.Header closeButton className='replace-close-button' data-cy="replace-modal-close">
          <Modal.Title className="replace-modal-title">Replace Material</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Find a suitable replacement for:&nbsp;
            <strong>
              {material?.name?.value} ({material?.partNo})
            </strong>
          </p>

          <CustomTable
            columns={columns}
            data={tableData}
            toolbar
            exportButton={false}
            enableSearch
            filename='Replace Material List'
            itemsPerPageCustom={false}
          />
        </Modal.Body>
        <Modal.Footer className="replace-modal-footer">
          <Button variant="secondary" className="replace-modal-cancel-btn  cancel-btn" onClick={onClose}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={confirmVisible}
        onHide={cancelConfirmation}
        size="lg"
        backdrop="static"
        keyboard={false}
        centered
      >
        <Modal.Header closeButton className='replace-close-button'>
          <Modal.Title className="replace-modal-title">Confirm Replacement</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to replace&nbsp;
            <strong>
              {material?.name?.value} ({material?.partNo})
            </strong>
            &nbsp;with&nbsp;
            <strong>
              {selectedReplacement?.item || selectedReplacement?.name || '-'} (
              {selectedReplacement?.partNo || selectedReplacement?.part_no || '-'})
            </strong>
            ?
          </p>
        </Modal.Body>
        <Modal.Footer className="replace-modal-footer">
          <Button variant="secondary" className="replace-modal-cancel-btn cancel-btn" onClick={cancelConfirmation}>
            No
          </Button>
          <Button variant="primary" className='cancel-btn' data-cy="replace-material-confirm" onClick={confirmReplacement}>
            Yes, Replace
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
